import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Package, Save, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const empty = { name: "", description: "", unit: "PZA", unit_price: "", sku: "", active: true };

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product or null
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, unit_price: String(p.unit_price) });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, unit_price: parseFloat(form.unit_price || 0) };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Producto creado");
      }
      setOpen(false);
      fetchProducts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Producto eliminado");
      fetchProducts();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const money = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

  return (
    <div data-testid="products-tab">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-bold uppercase text-xl tracking-tight">
          <Package className="inline w-5 h-5 text-[#F59E0B] mr-2 -mt-1" />
          Catálogo de Productos / Servicios
        </h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openCreate} className="btn-primary" data-testid="product-create-btn">
              <Plus className="w-4 h-4" /> Nuevo
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-lg rounded-none">
            <DialogHeader>
              <DialogTitle className="font-heading font-black uppercase">
                {editing ? "Editar producto" : "Nuevo producto"}
              </DialogTitle>
              <DialogDescription className="text-[#A1A1AA]">
                Estos productos aparecerán como sugerencias al cotizar.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="overline block mb-2">Nombre *</label>
                <input required className="input-base" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" />
              </div>
              <div>
                <label className="overline block mb-2">Descripción</label>
                <textarea rows={2} className="input-base resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="overline block mb-2">Unidad</label>
                  <input className="input-base" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="PZA / SERV / HR" data-testid="product-unit" />
                </div>
                <div className="col-span-2">
                  <label className="overline block mb-2">Precio Unitario (MXN) *</label>
                  <input type="number" step="0.01" required className="input-base" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="product-price" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">SKU / Clave (opcional)</label>
                <input className="input-base" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku" />
              </div>
              <DialogFooter>
                <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
                  <X className="w-4 h-4" /> Cancelar
                </button>
                <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="product-save-btn">
                  {saving ? "Guardando..." : "Guardar"} <Save className="w-4 h-4" />
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#2E2E33] bg-[#1F1F25]">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
          <div className="col-span-4">Producto / Servicio</div>
          <div className="col-span-4">Descripción</div>
          <div className="col-span-1">Unidad</div>
          <div className="col-span-2 text-right">P. Unit.</div>
          <div className="col-span-1 text-right">Acción</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#A1A1AA] font-mono text-sm">Cargando...</div>
        ) : products.length === 0 ? (
          <div className="p-16 text-center">
            <Package className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No hay productos. Crea el primero.</p>
          </div>
        ) : (
          products.map((p) => (
            <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] last:border-b-0 hover:bg-[#26262C]" data-testid={`product-row-${p.id}`}>
              <div className="md:col-span-4 font-heading font-bold text-white">{p.name} {p.sku && <span className="font-mono text-[10px] text-[#71717A] ml-2">[{p.sku}]</span>}</div>
              <div className="md:col-span-4 text-sm text-[#D4D4D8]">{p.description || "—"}</div>
              <div className="md:col-span-1 font-mono text-xs text-[#A1A1AA]">{p.unit}</div>
              <div className="md:col-span-2 text-right font-heading font-bold text-[#F59E0B]">{money(p.unit_price)}</div>
              <div className="md:col-span-1 flex md:justify-end gap-2">
                <button onClick={() => openEdit(p)} className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] px-2.5 py-2" data-testid={`product-edit-${p.id}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => del(p)} className="border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] px-2.5 py-2" data-testid={`product-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
